import { LegalLayout } from "@/components/LegalLayout";
import { LEGAL } from "@/legal/config";

export default function PrivacyPage() {
  const mail = <a href={`mailto:${LEGAL.supportEmail}`}>{LEGAL.supportEmail}</a>;
  return (
    <LegalLayout title="Privacy Policy" subtitle={`Effective ${LEGAL.effectiveDate}`} testId="privacy-page">
      <section>
        <p>{LEGAL.appName} ("we", "us") is an invoicing and expense-tracking service for field-service businesses, operated by {LEGAL.ownerName}. This policy explains what information we collect when you use the {LEGAL.appName} website and mobile app (together, the "Service"), how we use it, and the choices you have.</p>
      </section>
      <section>
        <h2>Information we collect</h2>
        <ul>
          <li><b>Account information</b> — your email address, a hashed password, and your business name, address, phone and logo.</li>
          <li><b>Customer records you enter</b> — names, email addresses, phone numbers, service addresses, services and prices for the clients you invoice. You are responsible for having the right to store this information about your customers.</li>
          <li><b>Invoices, receipts and expenses</b> — line items, amounts, tax rates, payment status, notes and photos of receipts you choose to upload.</li>
          <li><b>Billing information</b> — your subscription plan and status. Card details are collected and stored by Stripe, our payment processor; we never see your full card number.</li>
          <li><b>Technical data</b> — IP address, browser/device type and basic request logs needed to run and secure the Service. We do not use advertising trackers or sell data to data brokers.</li>
        </ul>
      </section>
      <section>
        <h2>How we use information</h2>
        <ul>
          <li>To provide the Service: generate PDF invoices and receipts, email them to the recipients you choose, calculate totals and reports.</li>
          <li>To manage your subscription and free trial, and to send service emails (receipts for your subscription, important account notices).</li>
          <li>To keep the Service secure — for example, rate-limiting failed sign-in attempts.</li>
          <li>To respond to your support requests.</li>
        </ul>
        <p>We do not sell your personal information and we do not use your customers' information for our own marketing.</p>
      </section>
      <section>
        <h2>Who we share information with</h2>
        <ul>
          <li><b>Stripe</b> — subscription payments (<a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a>).</li>
          <li><b>Resend</b> — delivers the invoice and receipt emails you send, including the recipient's email address and the PDF attachment.</li>
          <li><b>Cloud hosting and file storage providers</b> — store the database and uploaded files (logos, receipt photos) on our behalf.</li>
        </ul>
        <p>These providers process data only to deliver their services to us. We may also disclose information if required by law or to protect the rights and safety of {LEGAL.appName} and its users.</p>
      </section>
      <section>
        <h2>Data retention and deletion</h2>
        <p>Your data is kept for as long as your account exists. You can delete your account at any time from <b>Settings → Delete account</b> inside the app; this permanently removes your account, clients, invoices, expenses, receipts and logo, and cancels any active subscription. You can also email {mail} from your account address and we will delete your account within 30 days. Payment records held by Stripe are retained as required by tax and accounting law.</p>
      </section>
      <section>
        <h2>Security</h2>
        <p>Data is encrypted in transit (HTTPS). Passwords are stored hashed. Access to production systems is limited to the operator. No method of transmission or storage is 100% secure, so please use a strong, unique password.</p>
      </section>
      <section>
        <h2>Children</h2>
        <p>The Service is a business tool and is not directed to children under 13 (or 16 where applicable). We do not knowingly collect information from children.</p>
      </section>
      <section>
        <h2>Your rights</h2>
        <p>Depending on where you live you may have the right to access, correct, export or delete your personal information. Most of this you can do directly in the app (Settings, Clients, Reports → CSV export). For anything else, email {mail}.</p>
      </section>
      <section>
        <h2>Changes</h2>
        <p>We may update this policy as the Service evolves. We will post the new version here and update the effective date; material changes will be announced in the app.</p>
      </section>
      <section>
        <h2>Contact</h2>
        <p>{LEGAL.ownerName} · {mail}</p>
      </section>
    </LegalLayout>
  );
}
